import React, { useState, useEffect, useRef, useCallback } from "react";
import useKeypress from "../../hooks/useKeypress";
import useOnClickOutside from "../../hooks/useOnClickOutside";
import DOMPurify from "dompurify";

function InlineEdit(props) {
  let inputText = props.text;
  let showText = props.text;

  const [isInputActive, setIsInputActive] = useState(false);
  const [inputValue, setInputValue] = useState(inputText);

  const wrapperRef = useRef(null);
  const textRef = useRef(null);
  const inputRef = useRef(null);

  const enter = useKeypress("Enter");
  const esc = useKeypress("Escape");

  const { onSetText } = props;

  // check to see if the user clicked outside of this component
  useOnClickOutside(wrapperRef, () => {
    if (isInputActive) {
      onSetText(inputValue);
      setIsInputActive(false);
    }
  });

  const onEnter = useCallback(() => {
    if (enter) {
      onSetText(inputValue);
      setIsInputActive(false);
    }
  }, [enter, inputValue, onSetText]);

  const onEsc = useCallback(() => {
    if (esc) {
      setInputValue(props.text);
      setIsInputActive(false);
    }
  }, [esc, props.text]);

  // focus the cursor in the input field on edit start
  useEffect(() => {
    if (isInputActive) {
      inputRef.current.focus();
    }
  }, [isInputActive]);

  useEffect(() => {
    if (isInputActive) {
      // if Enter is pressed, save the text and close the editor
      onEnter();
      // if Escape is pressed, revert the text and close the editor
      onEsc();
    }
  }, [onEnter, onEsc, isInputActive]); // watch the Enter and Escape key presses

  const handleInputChange = useCallback(
    (event) => {
      // sanitize the input a little
      setInputValue(DOMPurify.sanitize(event.target.value));
    },
    [setInputValue]
  );

  const handleSpanClick = useCallback(
    () => setIsInputActive(true),
    [setIsInputActive]
  );

  if (showText.length > 0) {
    return (
      <span className="inline-text" ref={wrapperRef}>
        <span
          ref={textRef}
          onClick={handleSpanClick}
          className={`inline-text_copy inline-text_copy--${
            !isInputActive ? "active" : "hidden"
          }`}
        >
          {showText}
        </span>
        <input
          ref={inputRef}
          // set the width to the input length multiplied by the x height
          // it's not quite right but gets it close
          style={{ minWidth: Math.ceil(inputValue.length) + "ch" }}
          value={inputValue}
          onChange={handleInputChange}
          className={`inline-text_input inline-text_input--${
            isInputActive ? "active" : "hidden"
          }`}
        />
      </span>
    );
  } else {
    return (
      <div className="inline-text-empty">
        <div ref={wrapperRef}>
          <div
            ref={textRef}
            onClick={handleSpanClick}
            className={`inline-text-empty_copy inline-text-empty_copy--${
              !isInputActive ? "active" : "hidden"
            }`}
          >
            {showText}
          </div>
          <input
            ref={inputRef}
            // set the width to the input length multiplied by the x height
            // it's not quite right but gets it close
            style={{ minWidth: 15 + "ch", width: Math.floor(inputValue.length) + "ch" }}
            value={inputValue}
            onChange={handleInputChange}
            className={`inline-text-empty_input inline-text-empty_input--${
              isInputActive ? "active" : "hidden"
            }`}
          />
        </div>
        <div className="inline-edit-details">{props.label}</div>
      </div>
    );
  }
}

export default InlineEdit;
